import { PAGE_PERMISSIONS, type PermissionCode } from '@/constants/permissions';
import { ROUTES } from '@/constants/routes';
import { TOKEN, TOKEN_ACCESS, TOKEN_EXPIRES_AT } from '@/constants/token';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getSessions, postAuth } from '../api';
import { authQueryKeys } from '../constants';
import { authFormSchema, AuthFormValues } from '../schema';

const PERMISSION_ROUTE_MAP: {
  permissions: readonly PermissionCode[];
  route: string;
}[] = [
  { permissions: PAGE_PERMISSIONS.LAND, route: ROUTES.HOME },
  {
    permissions: PAGE_PERMISSIONS.COSMIC_MONITORING,
    route: `/${ROUTES.COSMIC_MONITORING}`,
  },
  { permissions: PAGE_PERMISSIONS.HARVEST, route: `/${ROUTES.HARVEST}` },
  { permissions: PAGE_PERMISSIONS.BURSE, route: `/${ROUTES.BIRJA}` },
  { permissions: PAGE_PERMISSIONS.BANK, route: `/${ROUTES.BANK}` },
  {
    permissions: PAGE_PERMISSIONS.AGROPORTAL,
    route: `/${ROUTES.AGROPORTAL}`,
  },
  {
    permissions: PAGE_PERMISSIONS.AGROSUBSIDIYA,
    route: `/${ROUTES.AGROSUBSIDIYA}`,
  },
  { permissions: PAGE_PERMISSIONS.SEEDS, route: `/${ROUTES.SEEDS}` },
  {
    permissions: PAGE_PERMISSIONS.MATERIAL_CONTROL,
    route: `/${ROUTES.MATERIAL_CONTROL}`,
  },
  { permissions: PAGE_PERMISSIONS.ROLES.INDEX, route: ROUTES.ROLES },
  { permissions: PAGE_PERMISSIONS.USERS.INDEX, route: ROUTES.USERS },
];

const getFirstAllowedRoute = (permissions: string[]): string => {
  for (const entry of PERMISSION_ROUTE_MAP) {
    if (entry.permissions.some((p) => permissions.includes(p))) {
      return entry.route;
    }
  }
  return `/${ROUTES.PROFILE}`;
};

export const useAuth = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const schema = authFormSchema(t);
  const form = useForm<AuthFormValues>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  useQuery({
    queryKey: [authQueryKeys.auth],
    queryFn: getSessions,
    onSuccess: (response) => {
      if (response?.session_id) {
        localStorage.setItem(TOKEN, response.session_id);
      }
    },
    enabled: !localStorage.getItem(TOKEN),
  });

  const { mutate, isLoading } = useMutation(
    (data: AuthFormValues) => postAuth(data),
    {
      onSuccess: async (res) => {
        localStorage.setItem(TOKEN_ACCESS, res.data?.access_token);
        if (res.data?.token_expires_at) {
          localStorage.setItem(TOKEN_EXPIRES_AT, res.data.token_expires_at);
        }
        const permissions: string[] = res.data?.permissions ?? [];
        const route = getFirstAllowedRoute(permissions);
        navigate(route);
        toast.success(t('successfully_logged_in'));
      },
    },
  );

  const onSubmit = (data: AuthFormValues) => mutate(data);

  return { t, form, onSubmit, isLoading };
};
